/**
 * The card-specific item TABLE, as data (backlog item 7, decision D9).
 *
 * When a list holds the items of ONE card, the table shows that card's
 * columns — every displayable element by default, or the columns a `table`
 * card layout chose (cardLayouts kind 'table', cascading user → collection →
 * card → this file's default). This module is the pure half: which columns,
 * in what order, which are frozen, and — for inline editing — whether a
 * given cell may be edited and, if not, the one sentence that says why.
 *
 * The editability rules are the FORM's rules, read from the same modules
 * the form and the selection bar read (`utils/permissions`,
 * `utils/recordPolicy`, the element's own options), because the owner's
 * requirement was that editing from the list enforces exactly what the form
 * enforces. The API is the authority either way — a cell the web thought
 * editable and the server refuses shows the server's sentence in the cell —
 * but a cell that LOOKS editable and then refuses is the failure this file
 * exists to prevent.
 *
 * Mirrors keepr-api utils/cardLayouts/validate.js (the table body): a column
 * names an `element` OR a `system` field; frozen columns are a leading run
 * of at most MAX_FROZEN; widths are 60-800px.
 */

import { canWriteCard } from '../../utils/permissions';
import { lockStateFor, recordPolicyFor } from '../../utils/recordPolicy';
import { isFormulaDriven } from '../../utils/formulaTokens';
import { normalizeId } from '../../utils/ids';
import { isLinkedRow, linkedReadOnlyReason } from '../../utils/linkedRecords';
import { confirmationsLabel } from '../../utils/confirmationValues';
import { GRANT_READ_ONLY_REASON, isSeeOnlyElement } from '../../utils/elementAccess';
import { isRankElement } from '../../utils/rank';
import { GROUP_DISPLAY_FIRST_DEFAULT, GROUP_DISPLAY_FIRST_MAX } from '../../utils/repeatingGroupEntries';
import { columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';

/**
 * A stored `display` the layout may KEEP, normalized: a group's column that
 * exists on the card, whether or not THIS person may read it. A layout is
 * shared; a person who is not shown a column must not erase another person's
 * choice by saving (the cell itself never reads a column they cannot see:
 * groupDisplayOf). Null for the count, or for a choice the card cannot honour
 * (not a group, the column gone).
 */
export function keptDisplay(def, display) {
	if (!isRepeatingGroup(def) || !display || display.as !== 'entries') return null;
	if (!columnsOf(def).some(column => column?.name === display.column)) return null;
	const first = Number.isInteger(display.first) && display.first >= 1 && display.first <= GROUP_DISPLAY_FIRST_MAX ? display.first : GROUP_DISPLAY_FIRST_DEFAULT;
	return { as: 'entries', column: display.column, first };
}

export const MAX_FROZEN = 3;
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 800;

/** The item's own fields a table may show, with their column headings. */
export const SYSTEM_COLUMNS = Object.freeze({
	title: 'Title',
	primaryDate: 'Primary date',
	updatedAt: 'Updated',
	createdAt: 'Created',
	// The source system's own created date (item.source.createdAt): shown,
	// never sorted — keepr-api TABLE_SORT_SYSTEM leaves it out on purpose.
	sourceCreatedAt: 'Created in source',
	tags: 'Tags',
	owner: 'Created by',
});

export const columnKey = column => (column?.system ? `sys:${column.system}` : `el:${column?.element}`);

/**
 * name → definition, the LAST declaration winning — the rule
 * getElementsForCard (utils/cards createElementResolver) merges a card's
 * chain by, so a child card's re-declaration of an inherited element is the
 * one the table reads. getElementsForCard already hands back one entry per
 * name; last-wins keeps an un-merged chain (parent's elements, then the
 * child's) reading the same way instead of silently taking the ancestor's —
 * which was how a child that made `hours` calculated still offered it as an
 * editable cell (card-table review R2).
 */
export const byNameOf = elementDefs => {
	const map = new Map();
	(elementDefs || []).forEach(def => {
		if (def?.name) map.set(def.name, def);
	});
	return map;
};

/** The fields a table layout's default `sort` may name besides the card's elements (the API's TABLE_SORT_SYSTEM). */
export const TABLE_SORT_SYSTEM = Object.freeze(['primaryDate', 'updatedAt', 'createdAt']);

/**
 * The stored columns the card can no longer draw: an element it has since
 * lost, or a system field keepr does not know. Nothing on the API cleans a
 * table layout when a card loses an element, so a stored row can name one —
 * and re-sending it is refused (400 unknown_element).
 */
export function staleTableColumns(columns, elementDefs) {
	const byName = byNameOf(elementDefs);
	return (Array.isArray(columns) ? columns : []).filter(column => {
		if (!column) return false;
		if (column.system) return !Object.prototype.hasOwnProperty.call(SYSTEM_COLUMNS, column.system);
		return Boolean(column.element) && !byName.has(column.element);
	});
}

/**
 * The columns whose stored `display` (a repeating group's cell, KPR-683) the
 * card can no longer honour: the element is no longer a group, or the column
 * it chose is gone or hidden from this person. The column stays; the editor
 * drops the display, which the API would refuse to be sent again.
 */
export function staleDisplays(columns, elementDefs) {
	const byName = byNameOf(elementDefs);
	return (Array.isArray(columns) ? columns : []).filter(column => {
		if (!column || !column.display || !column.element) return false;
		const def = byName.get(column.element);
		if (!def) return false;
		return !keptDisplay(def, column.display);
	});
}

/** Does a stored default sort still name something this card can be sorted by? */
export function tableSortIsValid(sort, elementDefs) {
	if (!sort?.field) return false;
	if (sort.direction !== undefined && sort.direction !== 'asc' && sort.direction !== 'desc') return false;
	return TABLE_SORT_SYSTEM.includes(sort.field) || byNameOf(elementDefs).has(sort.field);
}

/**
 * The columns a card gets when nobody has arranged its table: the title,
 * EVERY element in form order (inherited, then the card's own, then its
 * element sets' — getElementsForCard's order), the primary date and Updated.
 * Not capped: the table scrolls sideways inside its own container, which is
 * what it exists to do. The element the primary date stands for leaves the
 * element run (its column is the primary date's), and Updated is dropped when
 * it IS the primary date — the same two rules the mixed table has always run.
 */
export function defaultTableColumns(elementDefs, { primaryElement = null, primaryIsUpdated = false } = {}) {
	const columns = [{ system: 'title', frozen: true }];
	byNameOf(elementDefs).forEach((def, name) => {
		if (name === primaryElement) return;
		columns.push({ element: name });
	});
	columns.push({ system: 'primaryDate' });
	if (!primaryIsUpdated) columns.push({ system: 'updatedAt' });
	return columns;
}

/**
 * The columns to DRAW: a stored layout's, reconciled with the card as it is
 * now, or the default. Three reconciliations, each a promise the table makes
 * whatever a layout says:
 *   - an element the card has since lost is dropped, not drawn blank;
 *   - the title is always there, first, and frozen — it is the row's
 *     identity and the thing a reader scrolls back to;
 *   - frozen columns lead (the API already refuses a layout where they do
 *     not; this keeps an older row honest).
 */
export function resolveTableColumns(layoutBody, elementDefs, defaults) {
	const columns = Array.isArray(layoutBody?.columns) ? layoutBody.columns : null;
	if (!columns) return defaults;
	const byName = byNameOf(elementDefs);
	const seen = new Set();
	const kept = columns.filter(column => {
		if (!column || (!column.system && !column.element)) return false;
		if (column.system && !Object.prototype.hasOwnProperty.call(SYSTEM_COLUMNS, column.system)) return false;
		if (column.element && !byName.has(column.element)) return false;
		const key = columnKey(column);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
	const title = kept.find(column => column.system === 'title');
	const rest = kept.filter(column => column.system !== 'title');
	return [
		{ ...(title || {}), system: 'title', frozen: true },
		...rest.filter(column => column.frozen),
		...rest.filter(column => !column.frozen),
	];
}

/** A column's heading. */
export function columnLabel(column, byName, { primaryDateLabel = null } = {}) {
	if (column?.system === 'primaryDate' && primaryDateLabel) return primaryDateLabel;
	if (column?.system) return SYSTEM_COLUMNS[column.system] || column.system;
	const def = byName.get(column?.element);
	return def?.label?.singular || def?.name || column?.element || '';
}

// ---- the Columns editor ------------------------------------------------

/**
 * The editor's working list: every column the card COULD show, in the order
 * the current columns put them, the rest after in form order — each with
 * `shown` and `frozen`. The title is pinned first, always shown and frozen.
 */
export function editorRowsFor(currentColumns, elementDefs) {
	const byName = byNameOf(elementDefs);
	const rows = [];
	const seen = new Set();
	const push = (column, shown) => {
		const key = columnKey(column);
		if (seen.has(key)) return;
		seen.add(key);
		// A repeating group's cell (KPR-683) keeps what the layout chose, when
		// the card can still show it.
		const display = !column.system ? keptDisplay(byName.get(column.element), column.display) : null;
		rows.push({
			key,
			column: column.system ? { system: column.system } : { element: column.element },
			...(display ? { display } : {}),
			width: column.width,
			shown,
			frozen: column.system === 'title' ? true : !!(shown && column.frozen),
		});
	};
	push({ system: 'title' }, true);
	// A column the card can no longer draw is not offered: it would be
	// re-sent on Save and refused (see staleTableColumns).
	const stale = new Set(staleTableColumns(currentColumns, elementDefs));
	(currentColumns || []).forEach(column => { if (column && !stale.has(column)) push(column, true); });
	byName.forEach((def, name) => push({ element: name }, false));
	Object.keys(SYSTEM_COLUMNS).forEach(system => push({ system }, false));
	return rows;
}

/**
 * Editor rows → the stored body's `columns`. Only shown rows travel; frozen
 * ones are lifted to the front (the leading run the API requires) in the
 * order they sit; `frozen` is written only when true, as the API stores it.
 */
export function columnsFromEditorRows(rows) {
	const shown = (rows || []).filter(row => row.shown);
	const ordered = [...shown.filter(row => row.frozen), ...shown.filter(row => !row.frozen)];
	return ordered.map(row => ({
		...row.column,
		...(row.width ? { width: row.width } : {}),
		...(row.frozen ? { frozen: true } : {}),
		...(row.display ? { display: row.display } : {}),
	}));
}

export const frozenCount = rows => (rows || []).filter(row => row.shown && row.frozen).length;

/**
 * The rows in the order `keys` names them — a drag's answer (SortableList
 * hands back the new order of every id). The title stays first whatever the
 * order says, a key the rows do not have is ignored, and a row the order
 * leaves out keeps its place at the end; an order that changes nothing
 * returns `rows` itself.
 */
export function reorderEditorRows(rows, keys) {
	const byKey = new Map((rows || []).map(row => [row.key, row]));
	const title = (rows || []).filter(row => row.column?.system === 'title');
	const seen = new Set(title.map(row => row.key));
	const next = [...title];
	(keys || []).forEach(key => {
		const row = byKey.get(key);
		if (!row || seen.has(key)) return;
		seen.add(key);
		next.push(row);
	});
	(rows || []).forEach(row => { if (!seen.has(row.key)) next.push(row); });
	return next.every((row, index) => row === rows[index]) ? rows : next;
}

export function sameColumns(a, b) {
	return JSON.stringify(a || []) === JSON.stringify(b || []);
}

// ---- which cells may be edited in place ---------------------------------

/**
 * Can this cell be edited in the table, and if not, why? Returns
 * `{ editable: true }` or `{ editable: false, reason, opensItem? }`.
 * `opensItem` marks a row whose click opens the item instead (a record a
 * collection link brought in: editable at home, not here). Rich text IS
 * editable in a cell — in a modal (TableCellEditor).
 *
 * The order is the order a reader needs the answer in: whether they may
 * edit this record at all, then whether this record is still open, then
 * whether this particular value is anyone's to type.
 */
export function cellEditability({ column, def, item, collection, cardsById }) {
	if (column?.system) {
		return { editable: false, reason: SYSTEM_REASON[column.system] || 'Set by keepr.' };
	}
	if (!def) return { editable: false, reason: 'This field is no longer on the card.' };
	// A row a collection link brought into a merged list is read-only here —
	// the same rule as its ⋯ menu and the detail pane (utils/linkedRecords).
	// The list's own write grant says nothing about another collection's
	// record, so this comes before canWriteCard; Enter opens it at home.
	if (isLinkedRow(item)) return { editable: false, opensItem: true, reason: linkedReadOnlyReason(item) };
	// A "Me too" count (KPR-390) is nobody's to type, whatever their access:
	// each person says it, or takes it back, from the item, where Enter goes.
	if (def.dataType === 'confirmations') {
		return { editable: false, opensItem: true, reason: `Counted from the people who confirm — say “${confirmationsLabel(def) || 'Me too'}” from the item.` };
	}
	const cardId = normalizeId(item?.card_id);
	if (collection && !canWriteCard(collection, cardId)) {
		return { editable: false, reason: 'You cannot edit items of this card.' };
	}
	const lock = lockStateFor(item, recordPolicyFor(cardsById || {}, cardId));
	if (lock.locked) {
		return { editable: false, reason: `${lock.reason || 'This record is closed to edits.'} Add a correction from the item instead.` };
	}
	// An element rule holds this person to "see only" (KPR-509, the `grant`
	// branch): the API serves `access: 'see'` on the definition and refuses a
	// changed value with `element_read_only { reason: 'grant' }`. Before the
	// value-specific reasons below: whatever else is true of the value, it is
	// not this person's to change, and saying so is the whole answer.
	if (isSeeOnlyElement(def)) {
		return { editable: false, grant: true, reason: GRANT_READ_ONLY_REASON };
	}
	if (def.options?.sequence) {
		return { editable: false, reason: 'Numbered by keepr when the record was made. It never changes.' };
	}
	// A manual order (KPR-1396): keepr writes it when a row is moved. `rank`
	// tells a reorder (hooks/useRankMove) that the RECORD may be moved — every
	// reason above (access, a lock, a see-only rule) has already been asked.
	if (isRankElement(def)) {
		return { editable: false, rank: true, reason: 'The order people put items in. Drag the row, or use Move up and Move down in its ⋯ menu.' };
	}
	// A repeating group is a list of entries, edited from the item (KPR-680).
	if (def.dataType === 'repeating-group') {
		return { editable: false, opensItem: true, reason: 'Entries are added and changed from the item.' };
	}
	// A file is chosen and uploaded in the item's own form, never typed into a
	// cell (PR 10b); Enter opens the item.
	if (def.dataType === 'file') {
		return { editable: false, opensItem: true, reason: 'Files are added and changed from the item.' };
	}
	if (def.options?.drivenFrom) {
		return {
			editable: false,
			reason: isFormulaDriven(def)
				? 'Calculated by keepr from the other fields — nobody types this one.'
				: 'Kept up to date by an automation from other records — nobody types this one.',
		};
	}
	return { editable: true };
}

const SYSTEM_REASON = {
	title: 'The title is made from the card’s fields — edit those.',
	primaryDate: 'The primary date is one of the card’s dates, or when the item was made or changed.',
	updatedAt: 'Set by keepr when the item is saved.',
	createdAt: 'Set by keepr when the item was made.',
	tags: 'Change tags from the item (⋯ Tag…), or select items and use Tags….',
	owner: 'Whoever made the item. It never changes.',
};

/**
 * The first rule a cell edit would break, judged on the row AS IT WOULD
 * STAND (the API's own reading — required is asked of the merged map, not
 * of the one value sent). The edited element's own message wins; a message
 * about ANOTHER element means this edit made it required (requiredWhen) or
 * the record already breaks a rule, and either way the form is where it is
 * fixed, so the sentence says to open it.
 */
export function cellValidationMessage(errors, name, byName) {
	if (!errors) return null;
	if (errors[name]) return errors[name];
	const other = Object.keys(errors)[0];
	if (!other) return null;
	const label = byName.get(other)?.label?.singular || other;
	return `${errors[other]} Open the item to fill in ${label} too.`;
}
